import React from 'react';
import { Box, SpaceBetween } from '@cloudscape-design/components';

const HomeApprovalsItemsHelp = () => (
  <SpaceBetween size="s">
    <Box variant="h3">Decision and Review Items</Box>
    <Box>
      This table shows the selected decision or review queue. Decision Makers use
      <strong> Pending Decision</strong> for final decisions and appeal requests. Appeal rows show
      the participant name and current status. Reassessment moves to the case owner’s queue until review or a decision is needed. Regional Managers use
      <strong> Pending Review</strong> for application assessments, new intervention proposals,
      and proposed intervention changes waiting on RM review or returned by the Decision Maker.
    </Box>
    <Box>
      In ordinary Regional Manager review, open the item and choose <strong>Return to submitter</strong>
      or <strong>Submit for final decision</strong>. When the Decision Maker has requested changes,
      the only downward action is to review the Decision Maker note and <strong>Forward changes to
      submitter</strong>. The corrected item returns through Regional Manager review again.
    </Box>
    <Box>
      Use the table to identify which record to open next. <strong>Province</strong> shows the
      applicant&apos;s province, <strong>EI status</strong> shows the application assessment category or the intervention’s parent-plan classification,
      and <strong>Timeline target</strong> shows the due or overdue badge for the configured
      <strong>Program decision</strong> timing target for assessment and intervention decisions.
      Appeal requests have no automatic deadline in this column.
    </Box>
    <Box>
      EI status and review stage are different. Initial application verification establishes the
      funding classification. Interventions inherit their Action Plan’s classification without a
      repeat EI check. Review stage determines Pending Review or Pending Decision.
    </Box>
    <Box>
      Select the applicant or item name to review the full record and complete the next action
      there. Regional Manager review entries open the relevant review step. Final-decision entries
      open the application or case workspace in a decision-focused layout. For intervention
      proposals and changes, any letter preparation is a separate follow-up after the decision is
      recorded. A funded approval may then require the exact Client Funding Agreement and funding
      forms; CFA work never adds another review stage.
    </Box>
    <Box>
      An <strong>Appeal request</strong> opens a compact window over the relevant application or case. Minimise it when you need more room. Keep the original decision, authorize a direct
      change or send it for reassessment. Receiving an appeal leaves existing funding unchanged.
    </Box>
    <Box>
      The item details show only funded proposed payment items, grouped under the intervention
      type. Use the Tag column if you want to keep a personal follow-up marker in
      <strong>My Tagged Applications</strong>.
    </Box>
  </SpaceBetween>
);

HomeApprovalsItemsHelp.aiContext = `
Appeal queue entries follow the current work stage. Reassessment belongs to the current case owner; it is not a pending Decision Maker task until work returns for a decision. Matching ordinary tasks are replaced by one appeal entry per action queue, not counted twice.

You are assisting with the Pending Decision Items table on the NWAC ISET homepage.
Explain that Decision Makers use Pending Decision for submitted application assessments, new intervention proposals, and proposed intervention changes at final-decision review. Regional Managers use Pending Review for application assessments, new intervention proposals, and proposed intervention changes waiting on RM review or returned to RM after the Decision Maker requested changes. Regional Managers do not record final decisions.

Keep answers operational:
- Appeal requests also appear in Pending Decision, labelled with the participant and current status. Open the appeal in a compact window over the exact application/case to retain the decision, authorize a direct change or refer for reassessment. The record remains usable; minimise the window for more space. Supporting details and history are collapsed. Receipt does not change funding. Appeals have no automatic deadline in the Timeline target column.
- Mention the key columns: Province, EI status, and Timeline target.
- Explain that Timeline target uses the configured Program decision timing target, but the badge text is intentionally shortened to due/overdue wording.
- Explain that reviews and decisions are completed inside the workspace, not as inline table actions.
- Explain that the applicant/item name carries review or decision context so the target workspace opens in the relevant step.
- Explain the two downward paths: an RM return goes to the recorded submitter; a Decision Maker request goes to the RM first, who forwards it with a note, and the correction then passes RM review again.
- Explain that initial application EI verification establishes plan funding; interventions inherit the parent classification without another lookup at approval. Missing or inconsistent plan configuration must be corrected. Review-workflow stage controls queue ownership.
- Clarify that post-decision letter preparation and any exact application/Action Plan-linked Client Funding Agreement are separate from review and do not add another approval step. Zero-funding approvals have no CFA package.
- Clarify that the item breakdown shows only funded proposed payment items grouped under intervention type.
- Mention tagging only as a personal follow-up aid, not as part of the final decision itself.
`;

export default HomeApprovalsItemsHelp;
